import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, HeadContent, Link, Outlet, Scripts } from '@tanstack/react-router';
import { ThemeProvider as ThemeProviderBase, type ThemeProviderProps } from 'next-themes';
import { type FC, type ReactNode, useEffect } from 'react';
import { pushError } from '@/lib/faro';

import '@fontsource-variable/ibm-plex-sans';
import '@fontsource-variable/lora';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@/lib/fontawesome';
import appCss from '../styles.css?url';

function NotFound() {
  return (
    <div className="min-h-screen text-mesa-text bg-mesa-cream relative font-sans">
      {/* Green stripe */}
      <div className="h-2 bg-mesa-green" />

      {/* Content */}
      <div className="relative flex flex-col items-center justify-center min-h-[calc(100vh-8px)] px-8">
        <p className="text-xs text-mesa-muted-light font-mono tracking-wider mb-4">ERROR 404</p>
        <h1 className="text-4xl font-bold mb-4 tracking-tight">Page Not Found</h1>
        <p className="text-mesa-muted-light mb-8 text-center max-w-md">
          The page you're looking for doesn't exist or has been moved.
        </p>

        <Link to="/" className="px-6 py-3 bg-mesa-green text-white hover:bg-mesa-green-dark transition-colors">
          Back to Home
        </Link>
      </div>

      {/* Bottom stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-2 bg-mesa-green" />
    </div>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  useEffect(() => {
    pushError(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - Depot</title>
      </head>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          backgroundColor: '#f8f5f0',
          color: '#252220',
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          position: 'relative',
        }}
      >
        <div style={{ height: '0.5rem', backgroundColor: '#2d5a27' }} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 16px)',
            padding: '0 32px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: 12,
              color: '#666',
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              letterSpacing: '0.1em',
              marginBottom: 16,
            }}
          >
            ERROR
          </p>
          <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 16, letterSpacing: '-0.02em' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#666', marginBottom: 16, maxWidth: 400 }}>
            An unexpected error occurred. Please try again.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <pre
              style={{
                fontSize: 12,
                color: '#b91c1c',
                backgroundColor: 'rgba(185, 28, 28, 0.1)',
                padding: 16,
                marginBottom: 24,
                maxWidth: 500,
                overflow: 'auto',
                border: '1px solid rgba(185, 28, 28, 0.2)',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              }}
            >
              {error.message}
            </pre>
          )}

          <button
            type="button"
            onClick={reset}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2d5a27',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Try Again
          </button>
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '0.5rem',
            backgroundColor: '#2d5a27',
          }}
        />
      </body>
    </html>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Depot',
      },
    ],
    links: [
      { rel: 'icon', type: 'image/png', href: '/favicon.png' },
      {
        rel: 'stylesheet',
        href: appCss,
      },
    ],
  }),

  component: RootComponent,
});

// next-themes types ThemeProvider as a plain function instead of React.FC,
// which causes React 19 JSX to reject `children` as a valid prop.
const ThemeProvider = ThemeProviderBase as FC<ThemeProviderProps & { children?: ReactNode }>;

/**
 * Inline critical CSS that prevents FOUC during streaming SSR.
 *
 * The page is hidden (opacity:0) until the external Tailwind stylesheet loads
 * and the `opacity-100` class on <body> takes effect (higher specificity wins).
 * A 1s CSS-animation fallback ensures the page still reveals itself even if the
 * stylesheet fails to load entirely. Background colors are set per-theme so the
 * blank page matches the expected theme while hidden.
 */
const criticalCss = `
body { margin: 0; opacity: 0; background-color: #f8f5f0; animation: __fouc 0s 1s forwards; }
.dark body { background-color: #1a1918; }
@keyframes __fouc { to { opacity: 1; } }
`;

/**
 * Inline blocking script that resolves the theme before first paint, matching
 * next-themes' storage key ("theme") and the ThemeProvider's `attribute="class"`
 * configuration. Without this, SSR HTML renders without a theme class and then
 * flashes when next-themes hydrates client-side.
 */
const themeScript = `
(function(){try{var d=document.documentElement,t=localStorage.getItem("theme");if(t==="dark"||(t!=="light"&&window.matchMedia("(prefers-color-scheme:dark)").matches))d.classList.add("dark");else d.classList.remove("dark")}catch(e){}})();
`;

function RootComponent() {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: static constant, not user input */}
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: static constant, not user input */}
        <style dangerouslySetInnerHTML={{ __html: criticalCss }} />
        <HeadContent />
      </head>
      <body className="bg-mesa-cream text-mesa-text font-sans opacity-100">
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
          <Outlet />
        </ThemeProvider>
        <ReactQueryDevtools buttonPosition="bottom-left" />
        <Scripts />
      </body>
    </html>
  );
}
